/*
 * EVENT QUESTIONNAIRE — get-ready flow (SETTINGS-2026-08-25-105204 /svc seam;
 * agreed contract MY-2026-08-25-054534: everything below talks ONLY to the
 * my-api BFF — the FE never holds the settings service token).
 *
 * Behaviour:
 *  - Questions come from the BFF read (settings' resolved payload, pass-through
 *    snake_case). Choice questions (an options array — settings v1 is the
 *    self-described fitness set {value: 3|2|1}) render as radio choices; a
 *    question WITHOUT options falls back to a free-text input. Nothing is
 *    fabricated: only what the payload carries renders (PROCESS-3).
 *  - `required` is enforced client-side: Submit stays gated (amber guard, the
 *    HeatSelect D19 pattern) until every required question has an answer.
 *  - Submit POSTs each answered question via the BFF write (one answer per
 *    call, the contract shape). Any failure → an honest, RETRY-able error
 *    state; nothing is marked done locally on failure. On success the
 *    readiness-refresh bus re-reads the step state on return to Get ready —
 *    the server `submitted` flag is what confirms completion (local success
 *    navigation is optimistic UI only, never persisted).
 */
import { useCallback, useEffect, useMemo, useState, type CSSProperties } from 'react'
import { useLocation } from 'react-router-dom'
import {
  clearQuestionnaireAnswer, fetchQuestionnaire, submitQuestionnaireAnswer,
  type QuestionnaireOption, type QuestionnaireQuestion, type QuestionnaireStatus,
} from '../../data/myApi'
import { bumpReadinessRefresh } from '../../data/getReady'
import { eventQuestionnaireQuestions, questionAnswered, questionnaireComplete } from '../../data/participantConfig'
import { guardianClaimForTicket } from '../../data/guardianClaim'
import { flowEntryFromSession, rememberFlowEntry } from '../../data/flowTicketSession'
import { Button } from '../../components/ui'
import { IconBack } from '../../lib/icons'
import { useLocale, type Lang } from '../../i18n/useLocale'

// A single-choice / free-text answer is a scalar; a multiple_choice answer is
// an ARRAY of the selected option values (settings PR#310).
type OptionValue = string | number
type AnswerValue = OptionValue | OptionValue[]

/** Resolve an option's visible label — THE CONTRACT, not a shim (ratified
 *  both sides: my pin MY-2026-09-01-194520, settings confirm SETTINGS-2026-
 *  09-01-200559): `label_i18n` (Record locale→string) is the CANONICAL option
 *  wire shape, CLIENT-resolved at render time (the user's locale lives here;
 *  a server-flat label would bake one locale and break the Arabic path). Flat
 *  `label` is legacy-tolerated and wins when non-empty. Resolution order:
 *  `label` (non-empty) → `label_i18n[currentLocale]` → `label_i18n.en` →
 *  first label_i18n value → String(value). */
function optionLabel(
  value: OptionValue,
  opt: Pick<QuestionnaireOption, 'label' | 'label_i18n'> | null | undefined,
  lang: Lang,
): string {
  if (opt?.label != null && opt.label !== '') return opt.label
  const i18n = opt?.label_i18n
  if (i18n) {
    const pick = i18n[lang] ?? i18n.en ?? Object.values(i18n).find((v) => v != null && v !== '')
    if (pick != null && pick !== '') return pick
  }
  return String(value)
}

// Settings PR#310 render contract — the FOUR choice combos are keyed on qtype +
// `searchable`, NOT on list length (there is no length heuristic):
//   multiple_choice → multi-select; anything else with options → single-select.
const isMulti = (q: QuestionnaireQuestion): boolean => q.qtype === 'multiple_choice'
//   searchable true → type-ahead dropdown; absent/false → button/checkbox
//   choices (older payloads without the field render as buttons).
// AMENDED (BOARDROOM-2026-09-01-183926): the retired-qtype rule is relaxed —
// the LIVE resolved wire still self-describes dropdowns via legacy
// qtype:"dropdown" with NO searchable field, so the wire's own declaration is
// honoured (manage's Drop-down choice reaches us only via that field today).
const isSearchable = (q: QuestionnaireQuestion): boolean =>
  q.searchable === true || q.qtype === 'dropdown'

/** Normalise stored answer → the selected option values, as an array. */
function selectedValues(answer: AnswerValue | undefined): OptionValue[] {
  if (Array.isArray(answer)) return answer
  return answer == null ? [] : [answer]
}

/** Option type-ahead filter: substring match on the visible label, prefix
 *  matches first — the same interaction as the residence/city wizard typeahead
 *  (referenceData.ts `residenceMatches`/`cityMatches`). Matches on the SAME
 *  resolved label the user sees (flat label OR label_i18n — see optionLabel). */
function optionMatches(options: QuestionnaireOption[], query: string, lang: Lang): QuestionnaireOption[] {
  const q = query.trim().toLowerCase()
  if (!q) return options
  const lab = (o: QuestionnaireOption) => optionLabel(o.value, o, lang).toLowerCase()
  return options
    .filter((o) => lab(o).includes(q))
    .sort((a, b) => (lab(b).startsWith(q) ? 1 : 0) - (lab(a).startsWith(q) ? 1 : 0))
}

// Type-ahead list chrome — mirrors the wizard's listBox/listRow (ProfileWizard),
// re-tokenised to this surface's hairline/surface scale.
const listBoxStyle: CSSProperties = {
  background: 'var(--surface)', border: '1px solid var(--hairline-strong)',
  borderRadius: 9, marginTop: 6, overflow: 'hidden',
}
const listRowStyle: CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 9, width: '100%', border: 0,
  background: 'transparent', color: 'var(--text)', fontFamily: 'inherit', fontSize: 13.5,
  fontWeight: 600, textAlign: 'start', minHeight: 42, padding: '0 12px', cursor: 'pointer',
  borderBottom: '1px solid var(--hairline-07)',
}
const searchInputStyle: CSSProperties = {
  width: '100%', boxSizing: 'border-box', marginTop: 6, padding: '10px 12px',
  borderRadius: 9, border: '1px solid var(--hairline-strong)', background: 'transparent',
  color: 'var(--text)', fontFamily: 'inherit', fontSize: 13.5, outline: 'none',
}
/** Shared teal chip chrome — the multi-select pick chips and the About-{child}
 *  attribution chip differ only in layout/size overrides (RTL-safe: logical
 *  inline padding). */
const chipStyle: CSSProperties = {
  paddingInline: '10px', borderRadius: 999, fontWeight: 700,
  background: 'var(--tint-teal)', border: '1px solid var(--accent)',
}
const choiceLabelStyle = (selected: boolean): CSSProperties => ({
  display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer',
  padding: '10px 12px', borderRadius: 9, fontSize: 13.5,
  border: selected ? '1px solid var(--accent)' : '1px solid var(--hairline-strong)',
  background: selected ? 'var(--tint-teal)' : 'transparent',
})

interface ChoiceRenderProps {
  question: QuestionnaireQuestion
  answer: AnswerValue | undefined
  onAnswer: (value: AnswerValue) => void
}

/** Combo 1 & 3 — button/radio (single) or checkbox (multi) choices. */
function ChoiceButtons({ question, answer, onAnswer }: ChoiceRenderProps) {
  const { lang } = useLocale()
  const options = question.options ?? []
  const multi = isMulti(question)
  const picked = selectedValues(answer)
  const isSel = (v: OptionValue) => picked.some((s) => s === v)
  const toggle = (v: OptionValue) => {
    if (!multi) { onAnswer(v); return }
    onAnswer(isSel(v) ? picked.filter((s) => s !== v) : [...picked, v])
  }
  return (
    <div
      role={multi ? 'group' : 'radiogroup'}
      aria-label={question.prompt}
      className="col"
      style={{ gap: 8, marginTop: 6 }}
    >
      {options.map((o) => {
        const selected = isSel(o.value)
        return (
          <label key={String(o.value)} style={choiceLabelStyle(selected)}>
            <input
              type={multi ? 'checkbox' : 'radio'}
              name={`q-${question.question_id}`}
              value={String(o.value)}
              checked={selected}
              onChange={() => toggle(o.value)}
            />
            {optionLabel(o.value, o, lang)}
          </label>
        )
      })}
    </div>
  )
}

/** Combo 2 & 4 — type-ahead dropdown, single- or multi-select. Commit is by
 *  click OR Enter (Enter commits the first filtered row), matching the wizard
 *  residence/city comboboxes; a multi-select keeps the box open and clears the
 *  query on each pick so the next search starts fresh. */
function ChoiceTypeahead({ question, answer, onAnswer }: ChoiceRenderProps) {
  const { t, lang } = useLocale()
  const options = question.options ?? []
  const multi = isMulti(question)
  const [query, setQuery] = useState('')
  const [open, setOpen] = useState(false)

  const picked = selectedValues(answer)
  const isSel = (v: OptionValue) => picked.some((s) => s === v)
  const optById = (v: OptionValue) => options.find((o) => o.value === v) ?? null

  // Single-select shows the committed label in the field when the box is closed;
  // multi-select keeps the field a live search box (picks show as chips).
  const singleLabel = !multi && picked.length > 0
    ? optionLabel(picked[0], optById(picked[0]), lang)
    : ''
  // Multi shows the live query only while the list is open — on Escape/blur
  // the box clears so leftover search text never lingers beside the chips.
  const fieldValue = multi ? (open ? query : '') : (open ? query : singleLabel)

  const rows = open ? optionMatches(options, query, lang) : []

  const commit = (o: QuestionnaireOption) => {
    if (multi) {
      onAnswer(isSel(o.value) ? picked.filter((s) => s !== o.value) : [...picked, o.value])
      setQuery('') // next pick starts fresh; the box stays open
    } else {
      onAnswer(o.value)
      setQuery('')
      setOpen(false)
    }
  }

  return (
    <div style={{ position: 'relative', marginTop: 6 }}>
      {/* Committed multi-select picks as removable chips (RTL-safe: flex + gap,
          logical inline padding). */}
      {multi && picked.length > 0 && (
        <div className="row" style={{ flexWrap: 'wrap', gap: 6, marginBottom: 6 }}>
          {picked.map((v) => {
            const label = optionLabel(v, optById(v), lang)
            return (
              <span
                key={String(v)}
                data-testid={`questionnaire-chip-${question.question_id}-${v}`}
                className="row"
                style={{ ...chipStyle, alignItems: 'center', gap: 6, paddingBlock: 4, fontSize: 12.5 }}
              >
                {label}
                <button
                  type="button"
                  aria-label={t('quest.chip.remove', { label })}
                  onClick={() => onAnswer(picked.filter((s) => s !== v))}
                  style={{ border: 0, background: 'none', color: 'inherit', cursor: 'pointer', fontSize: 14, lineHeight: 1, padding: 0 }}
                >
                  ×
                </button>
              </span>
            )
          })}
        </div>
      )}
      <input
        type="text"
        role="combobox"
        aria-expanded={open}
        aria-controls={`questionnaire-search-list-${question.question_id}`}
        aria-autocomplete="list"
        aria-label={question.prompt}
        data-testid={`questionnaire-search-${question.question_id}`}
        value={fieldValue}
        placeholder={t('quest.search.placeholder')}
        onChange={(e) => { setQuery(e.target.value); setOpen(true) }}
        // Opening a closed single-select seeds the query with its committed
        // label so the user edits from the current pick.
        onFocus={() => { if (!multi && !open) setQuery(singleLabel); setOpen(true) }}
        // The list rows preventDefault on mousedown (below), so a click never
        // blurs the input — blur here only fires on a genuine focus-leave.
        onBlur={() => setOpen(false)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && rows.length > 0) { e.preventDefault(); commit(rows[0]) }
          else if (e.key === 'Escape') { setOpen(false) }
        }}
        style={searchInputStyle}
      />
      {open && (
        <div
          role="listbox"
          id={`questionnaire-search-list-${question.question_id}`}
          aria-multiselectable={multi || undefined}
          data-testid={`questionnaire-search-list-${question.question_id}`}
          style={listBoxStyle}
        >
          {rows.length === 0 ? (
            <div style={{ ...listRowStyle, cursor: 'default', color: 'var(--text-secondary)' }}>
              {t('quest.search.noMatch')}
            </div>
          ) : rows.map((o) => {
            const selected = isSel(o.value)
            return (
              <button
                key={String(o.value)}
                type="button"
                role="option"
                aria-selected={selected}
                // Keep the input focused so a pick can't be lost to a blur.
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => commit(o)}
                style={{ ...listRowStyle, background: selected ? 'var(--tint-teal)' : 'transparent' }}
              >
                <span aria-hidden style={{ width: 16, flex: 'none', color: 'var(--accent)' }}>{selected ? '✓' : ''}</span>
                <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {optionLabel(o.value, o, lang)}
                </span>
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}

function QuestionBlock({ question, answer, onAnswer }: ChoiceRenderProps) {
  const { t } = useLocale()
  const options = question.options ?? null
  const hasOptions = !!options && options.length > 0
  // Card polish (BOARDROOM-2026-09-01-183926 item 4): the question header sits
  // FULLY INSIDE the card — no legend straddling the border. The fieldset keeps
  // its group semantics; aria-labelledby ties the group to the question text.
  const headerId = `questionnaire-question-header-${question.question_id}`
  return (
    <fieldset
      data-testid={`questionnaire-question-${question.question_id}`}
      aria-labelledby={headerId}
      style={{ border: '1px solid var(--hairline-12)', borderRadius: 'var(--r-card)', padding: '14px 16px', margin: 0, background: 'var(--surface)' }}
    >
      <div id={headerId} style={{ fontSize: 14, fontWeight: 700 }}>
        {question.prompt}
        {question.required && (
          <span aria-hidden style={{ color: 'var(--warn)', marginInlineStart: 4 }}>*</span>
        )}
      </div>
      {hasOptions && isMulti(question) && (
        <p className="secondary" style={{ fontSize: 12, margin: '4px 4px 0' }}>{t('quest.multi.hint')}</p>
      )}
      {hasOptions ? (
        isSearchable(question)
          ? <ChoiceTypeahead question={question} answer={answer} onAnswer={onAnswer} />
          : <ChoiceButtons question={question} answer={answer} onAnswer={onAnswer} />
      ) : (
        // No option set on the wire → free-text fallback (never an invented set).
        <input
          type="text"
          aria-label={question.prompt}
          value={answer == null || Array.isArray(answer) ? '' : String(answer)}
          onChange={(e) => onAnswer(e.target.value)}
          style={{
            width: '100%', boxSizing: 'border-box', marginTop: 6, padding: '10px 12px',
            borderRadius: 9, border: '1px solid var(--hairline-strong)', background: 'transparent',
            color: 'var(--text)', fontFamily: 'inherit', fontSize: 13.5,
          }}
        />
      )}
    </fieldset>
  )
}

/** PREFILL (WU PROJ-0062-WU-PREFILL-01): seed answers from settings'
 *  per-question `answer_value` (#630: the stored V62 jsonb `{value, label?,
 *  labels?}`, present only when answered). A stored choice that is no longer
 *  an option is DROPPED (never shown as a phantom pick); free text is kept as
 *  stored. Questions without a usable stored value start empty, as before. */
export function prefillAnswers(questions: QuestionnaireQuestion[]): Record<string, AnswerValue> {
  const out: Record<string, AnswerValue> = {}
  for (const q of questions) {
    if (!q.answered || q.answer_value == null || typeof q.answer_value !== 'object') continue
    const raw = (q.answer_value as { value?: unknown }).value
    const opts = q.options ?? []
    const isOpt = (v: unknown): v is OptionValue =>
      (typeof v === 'string' || typeof v === 'number') && (opts.length === 0 || opts.some((o) => o.value === v))
    if (Array.isArray(raw)) {
      const picks = raw.filter(isOpt)
      if (picks.length > 0) out[q.question_id] = picks
    } else if (isOpt(raw)) {
      if (typeof raw === 'string' && raw.trim() === '') continue
      out[q.question_id] = raw
    }
  }
  return out
}

/** Order-insensitive equality for multi-select picks; strict otherwise
 *  (free text compared trimmed — what submit sends). */
export function sameAnswer(a: AnswerValue | undefined, b: AnswerValue | undefined): boolean {
  if (a === undefined || b === undefined) return a === b
  if (Array.isArray(a) || Array.isArray(b)) {
    if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) return false
    const key = (v: OptionValue) => `${typeof v}:${v}`
    const sa = [...a].map(key).sort()
    const sb = [...b].map(key).sort()
    return sa.every((v, i) => v === sb[i])
  }
  if (typeof a === 'string' && typeof b === 'string') return a.trim() === b.trim()
  return a === b
}

export function QuestionnaireFlow({ eventName, eventId, idToken, base, onBack, onDone }: {
  eventName: string
  eventId: string
  idToken: string
  base: string
  onBack: () => void
  onDone: () => void
}) {
  const { t, dir, lang } = useLocale()

  // GUARDIAN-MODE FRAMING (BOARDROOM-2026-09-05-101707, Duncan live test):
  // organiser-authored questions are second person ("…your fitness…") and are
  // NEVER rewritten here (organiser wording is theirs) — instead the page
  // frames WHO the answers are about when the seat is a child's. Subject
  // resolution copies the WaiverFlow guardian order, from EXISTING context
  // only (no new query param):
  //  1. Get-ready's per-holding route state (holderKind/holderName ride
  //     alongside ticketId in onStepAction — holderKind 'child' ⇒ a parent/
  //     guardian is answering for the named child),
  //  1b. on a mount WITHOUT route state, the sessionStorage entry capture
  //     from a prior visit (the shared flowTicketSession rail, mirroring
  //     WaiverFlow's BOARDROOM-174911 refresh survival),
  //  2. the device-local GUARDIAN-CLAIM record for the entry ticket (S2
  //     slice): the wire may carry no child identity for a guardian-claimed
  //     seat, but the claim record still names the child. SUPPRESSED when the
  //     entry carried an EXPLICIT holderKind 'self' — the wire saying "this
  //     seat is the caller's own" outranks a lingering device-local record
  //     (revoke → re-invite → self-claim staleness; the PR#124 stale-record
  //     guard, GetReady's localChild precedent).
  // A NAMED child is required — unnamed/unresolved ⇒ the plain self render,
  // never invented. Honest degradation: only a genuinely FRESH entry (new
  // tab / other device, where neither route state nor the session capture
  // exists) degrades to the self render — display-only, answers unaffected.
  // NOTE FOR MERGE: the about-{child} attribution chip below mirrors the
  // get-ready checklist rework's treatment (built in parallel) — fold into a
  // shared helper when both land.
  const location = useLocation()
  const routeState = (location.state ?? {}) as {
    ticketId?: string
    holderKind?: string
    holderName?: string | null
  }
  // Entry framing, seeded ONCE (useState initializer — the ChildConfirm rec
  // idiom): route state when it carries a ticket, else the prior visit's
  // session capture. Never re-read per keystroke.
  const [entry] = useState<{ ticketId?: string; holderKind?: string; holderName?: string | null }>(
    () => (routeState.ticketId ? routeState : flowEntryFromSession('questionnaire', eventId) ?? {}),
  )
  // Capture on arrival (WaiverFlow's entry-capture posture) so a hard refresh
  // recovers the framing above.
  useEffect(() => {
    if (routeState.ticketId) {
      rememberFlowEntry('questionnaire', eventId, {
        ticketId: routeState.ticketId,
        holderKind: routeState.holderKind,
        holderName: routeState.holderName ?? null,
      })
    }
  }, [eventId, routeState.ticketId, routeState.holderKind, routeState.holderName])
  const routeChild = entry.holderKind === 'child' ? (entry.holderName ?? '').trim() : ''
  // Claim-record fallback: short-circuits BEFORE the localStorage read when
  // the wire already names the child OR explicitly says 'self'; memoised on
  // the (stable) entry so the store is read once, not per keystroke.
  const claimChild = useMemo(() => {
    if (routeChild || entry.holderKind === 'self' || !entry.ticketId) return ''
    return (guardianClaimForTicket(entry.ticketId)?.childName ?? '').trim()
  }, [routeChild, entry])
  const guardianChild: string | null = routeChild || claimChild || null

  const [status, setStatus] = useState<QuestionnaireStatus | null>(null)
  const [loadState, setLoadState] = useState<'loading' | 'ready' | 'error'>('loading')
  const [answers, setAnswers] = useState<Record<string, AnswerValue>>({})
  // The prefilled snapshot: submit re-sends ONLY answers that differ from it.
  const [prefilled, setPrefilled] = useState<Record<string, AnswerValue>>({})
  // A COMPLETE questionnaire can be reopened to edit its prefilled answers.
  const [editing, setEditing] = useState(false)
  const [submitState, setSubmitState] = useState<'idle' | 'submitting' | 'error'>('idle')

  const load = useCallback(() => {
    setLoadState('loading')
    let cancelled = false
    fetchQuestionnaire(idToken, base, eventId)
      .then((q) => {
        if (cancelled) return
        const seed = prefillAnswers(q.questions ?? [])
        setStatus(q)
        setAnswers(seed)
        setPrefilled(seed)
        setLoadState('ready')
      })
      .catch(() => { if (!cancelled) setLoadState('error') })
    return () => { cancelled = true }
  }, [idToken, base, eventId])

  useEffect(() => load(), [load])

  // The EVENT questionnaire's own questions: the fitness_self row is a
  // PROFILE-LEVEL field asked by the profile wizard (boardroom ruling
  // 2026-10-01) — never rendered, submitted or counted here.
  const questions = useMemo(() => eventQuestionnaireQuestions(status?.questions), [status])
  // A free-text answer of pure whitespace is NOT an answer; a radio pick (or 0)
  // always is; a multi-select is answered once at least one option is picked.
  const answered = (q: QuestionnaireQuestion) => {
    const a = answers[q.question_id]
    if (a === undefined) return false
    if (Array.isArray(a)) return a.length > 0
    return typeof a !== 'string' || a.trim() !== ''
  }
  // COMPLETE = every REQUIRED non-fitness question answered server-side by
  // its OWN per-question signal (questionnaireComplete). The resolved-level
  // `submitted` is never read: it is some(answered) and settings #630 carries
  // a fitness answer from ANY event onto the fitness row.
  // The "completed" panel additionally needs SOMETHING to have been done: with
  // no required question it shows only once a non-fitness question carries an
  // answer — otherwise the optional form renders (never "your answers are in"
  // when none are).
  const complete =
    questionnaireComplete(status) &&
    (questions.some((q) => q.required === true) || questions.some((q) => questionAnswered(q)))
  // A required question already answered SERVER-side (its per-question signal)
  // does not gate Submit — only the outstanding ones do.
  const missingRequired = useMemo(
    () => questions.filter((q) => q.required && !answered(q) && !questionAnswered(q)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [questions, answers],
  )

  const submit = async () => {
    setSubmitState('submitting')
    try {
      // One answer per BFF call (the contract shape). Sequential — settings'
      // /svc write is an upsert, so a retry after a mid-flight failure simply
      // re-records; nothing is fabricated on failure.
      for (const q of questions) {
        if (!answered(q)) {
          // PREFILL follow-up: an OPTIONAL answer that was prefilled and is now
          // emptied is CLEARED server-side (answer_value {}), not left stored.
          if (!q.required && prefilled[q.question_id] !== undefined) {
            await clearQuestionnaireAnswer(idToken, base, eventId, q.question_id)
          }
          continue // optional + unanswered → nothing to record
        }
        const a = answers[q.question_id]
        // PREFILL: an answer left exactly as prefilled is already stored —
        // only CHANGED answers are re-sent.
        if (sameAnswer(a, prefilled[q.question_id])) continue
        // Multi-select → the array of picks (verbatim); free text → trimmed;
        // single choice → the scalar. The wire serialisation lives in
        // submitQuestionnaireAnswer (see its ⚠ multi-select note).
        const value = Array.isArray(a) ? a : (typeof a === 'string' ? a.trim() : a!)
        // Display labels ride alongside choice values (settings answer-shape v1
        // SHOULD: the admin CSV then prints "Small; Medium" instead of raw
        // values). Free text carries none; unknown values fall back to the
        // value string via optionLabel, so labels never desync from picks.
        const opts = q.options ?? []
        const labelOf = (v: OptionValue) =>
          optionLabel(v, opts.find((o) => o.value === v), lang)
        const labels = opts.length === 0 ? undefined
          : Array.isArray(a) ? a.map(labelOf)
          : typeof a !== 'string' || opts.some((o) => o.value === a) ? [labelOf(a as OptionValue)]
          : undefined
        await submitQuestionnaireAnswer(idToken, base, eventId, q.question_id, value, labels)
      }
      // All required answered + stored → the step completes locally; the server
      // `submitted` flag confirms on the next get-ready read.
      bumpReadinessRefresh()
      onDone()
    } catch {
      setSubmitState('error')
    }
  }

  return (
    <div className="col" style={{ gap: 16 }}>
      {/* Back "‹ {eventName}" — the HeatSelect D15 chrome */}
      <div className="row" style={{ justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
        <button onClick={onBack} className="row gap-6" style={{ background: 'none', border: 'none', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 13, padding: 0 }}>
          {/* The back arrow encodes direction — mirrored in code under RTL
              (README §RTL), never left as a physical left-pointing glyph. */}
          <span aria-hidden style={{ display: 'inline-flex', transform: dir === 'rtl' ? 'scaleX(-1)' : undefined }}>
            <IconBack size={16} />
          </span>
          {' '}{eventName}
        </button>
      </div>

      <div>
        {/* About-{child} attribution — the checklist-rework treatment on this
            surface (see the merge note above): guardian seats name WHO this
            page is about, right on the head. */}
        {guardianChild && (
          <span
            data-testid="questionnaire-subject-attribution"
            style={{ ...chipStyle, display: 'inline-block', paddingBlock: 3, fontSize: 12 }}
          >
            {t('quest.guardian.about', { child: guardianChild })}
          </span>
        )}
        <h1 style={{ fontSize: 22, margin: '4px 0 2px' }}>{t('quest.head.title')}</h1>
        <p className="secondary" style={{ fontSize: 13, margin: 0 }}>
          {t('quest.head.sub')}
        </p>
        {Object.keys(prefilled).length > 0 && (
          <p data-testid="questionnaire-prefilled-note" className="secondary" style={{ fontSize: 12, margin: '6px 0 0' }}>
            {t('quest.prefilled')}
          </p>
        )}
      </div>

      {loadState === 'loading' && (
        <p className="secondary" role="status" style={{ fontSize: 13 }}>{t('quest.loading')}</p>
      )}
      {loadState === 'error' && (
        <div className="col" style={{ gap: 10 }}>
          <p role="alert" className="secondary" style={{ fontSize: 13, margin: 0 }}>
            {t('quest.loadError')}
          </p>
          <div className="row" style={{ gap: 10 }}>
            <Button variant="ghost" onClick={onBack}>{t('flow.back')}</Button>
            <Button onClick={() => load()}>{t('quest.retry')}</Button>
          </div>
        </div>
      )}

      {loadState === 'ready' && status && (!status.active || !status.show || questions.length === 0) && (
        // Shouldn't normally be reachable (the step is absent when inactive, or
        // when fitness_self is its only question) — an honest empty state
        // rather than a fabricated question list.
        <div role="status" data-testid="questionnaire-inactive" style={{ border: '1px dashed var(--text-faint)', borderRadius: 'var(--r-card)', padding: 20, color: 'var(--text-secondary)', fontSize: 13 }}>
          {t('quest.inactive', { event: eventName })}
        </div>
      )}

      {loadState === 'ready' && status?.active && status.show && questions.length > 0 && complete && !editing && (
        <div className="col" style={{ gap: 10 }}>
          <div role="status" data-testid="questionnaire-submitted" style={{ border: '1px solid var(--accent)', background: 'var(--tint-teal)', borderRadius: 'var(--r-card)', padding: '14px 16px', fontSize: 13.5, fontWeight: 700 }}>
            {t('quest.submitted')}
          </div>
          <div className="row" style={{ gap: 10 }}>
            <Button variant="ghost" onClick={onBack}>{t('flow.back')}</Button>
            {Object.keys(prefilled).length > 0 && (
              <Button variant="ghost" onClick={() => setEditing(true)}>{t('quest.edit')}</Button>
            )}
          </div>
        </div>
      )}

      {loadState === 'ready' && status?.active && status.show && questions.length > 0 && (!complete || editing) && (
        <>
          {/* Guardian framing banner — page-level, ABOVE the organiser's
              questions: their second-person wording renders verbatim below,
              so the reframe ("answer about the child") must land first. */}
          {guardianChild && (
            <div
              role="note"
              data-testid="questionnaire-guardian-banner"
              style={{
                border: '1px solid var(--accent)', background: 'var(--tint-teal)',
                borderRadius: 'var(--r-card)', padding: '12px 16px',
              }}
            >
              <div style={{ fontSize: 13.5, fontWeight: 700 }}>
                {t('quest.guardian.title', { child: guardianChild })}
              </div>
              <div className="secondary" style={{ fontSize: 12.5, marginTop: 2 }}>
                {t('quest.guardian.sub', { child: guardianChild })}
              </div>
            </div>
          )}
          <div className="col" style={{ gap: 12 }}>
            {questions.map((q) => (
              <QuestionBlock
                key={q.question_id}
                question={q}
                answer={answers[q.question_id]}
                onAnswer={(value) => setAnswers((a) => ({ ...a, [q.question_id]: value }))}
              />
            ))}
          </div>

          {/* Required gate — the sticky amber guard (HeatSelect D19 pattern). */}
          {missingRequired.length > 0 && (
            <div
              role="status"
              data-testid="questionnaire-required-guard"
              style={{
                position: 'sticky', bottom: 0, display: 'flex', alignItems: 'center', gap: 8,
                padding: '10px 14px', background: 'var(--warn-bg)', color: 'var(--warn)',
                borderRadius: 8, fontSize: 12.5, fontWeight: 600,
              }}
            >
              ⚠ {missingRequired.length === 1 ? t('quest.required.one') : t('quest.required.other', { n: missingRequired.length })}
            </div>
          )}

          {submitState === 'error' && (
            <p role="alert" style={{ fontSize: 12.5, color: 'var(--warn)', margin: 0 }}>
              {t('quest.submitError')}
            </p>
          )}

          <div className="row" style={{ gap: 10 }}>
            <Button variant="ghost" onClick={onBack}>{t('flow.back')}</Button>
            <div style={{ flex: 1 }}>
              <Button
                full
                disabled={missingRequired.length > 0 || submitState === 'submitting'}
                onClick={() => { void submit() }}
              >
                {submitState === 'submitting' ? t('quest.saving') : t('quest.submit')}
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  )
}
